import API from "../../services/request";
import { API_PATH } from "../../utils/config";

// Over-request so de-duplication still leaves enough distinct swatches to
// fill the "From your image" row.
const REQUEST_COUNT = 12;
const MAX_SWATCHES = 8;
// Two colors within this Euclidean RGB distance read as visually
// indistinguishable in a 32px swatch -- collapse them into one.
const DEDUPE_DISTANCE = 24;

const hexToRgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const rgbDistance = (a, b) =>
  Math.sqrt(a.reduce((sum, v, i) => sum + (v - b[i]) ** 2, 0));

// Drop any color that's a near-duplicate of one already kept, preserving the
// prominence-sorted order ColorThief returns them in.
export const dedupeSimilarColors = (hexes, distance = DEDUPE_DISTANCE) => {
  const kept = [];
  const keptRgb = [];
  for (const hex of hexes) {
    const rgb = hexToRgb(hex);
    if (keptRgb.every((k) => rgbDistance(k, rgb) >= distance)) {
      kept.push(hex);
      keptRgb.push(rgb);
    }
  }
  return kept;
};

// Extract a "From your image" swatch row (deduped, prominence-sorted, capped
// to MAX_SWATCHES) for the given wallpaper image URL. Resolves [] on any
// failure (broken URL, decode failure, network error) -- callers hide the
// row silently on an empty result, never surface an error.
//
// Runs SERVER-SIDE (POST GALLERY_THEME_WALLPAPER_PALETTE) rather than via a
// client-side <canvas> extractor: our media bucket sends no
// Access-Control-Allow-Origin header, so a browser-side
// `img.crossOrigin = "Anonymous"` fetch fails CORS for every real wallpaper
// image (measured live on web-dev.iyuga.net -- a console CORS error, "From
// your image" never renders). The backend has no such restriction fetching
// its own bucket.
export const extractGalleryImagePalette = async (url) => {
  if (!url) return [];
  try {
    const response = await API.post(API_PATH.GALLERY_THEME_WALLPAPER_PALETTE(), {
      image_url: url,
      count: REQUEST_COUNT,
    });
    const body = response?.data;
    const hexes = (body?.data ?? body)?.colors;
    if (!Array.isArray(hexes)) return [];
    return dedupeSimilarColors(hexes).slice(0, MAX_SWATCHES);
  } catch {
    return [];
  }
};
